import { useIsProfileOwner } from '../components/v2BaseComponents/Permissions/hooks';
import { getIntl } from '../utils/intl';
import { useIsProfilePaid } from './api/useIsProfilePaid';
import { useAppType } from './useAppType';
import { useIsMobileLayout } from './useIsMobileLayout';
import usePersistedKeyValueStore from './usePersistedKeyValueStore';

const intl = getIntl('use-profile-subscribe-banner', {
    message: 'Subscribe to the family plan',
    buttonText: 'Learn More',
});

export type UseProfileSubscribeBannerArgs = {
    profileId: string;
};

export type UseProfileSubscribeBannerReturnValue = {
    canDismiss: boolean;
    onDismiss: () => void;
    shouldShow: boolean;
    messaging: {
        message: string;
        buttonText: string;
    };
};

export const useProfileSubscribeBanner = ({
    profileId,
}: UseProfileSubscribeBannerArgs): UseProfileSubscribeBannerReturnValue => {
    const appType = useAppType();
    const isMobile = useIsMobileLayout();
    const isProfileOwner = useIsProfileOwner(profileId);
    const dismissedState = usePersistedKeyValueStore({
        category: 'profile-subscribe-banner',
        key: 'should-show-banner',
        expiration: 7 * 24 * 60 * 1000, // 7 days
        defaultValue: false,
    });
    const profilePaymentStatus = useIsProfilePaid({ profileId });
    const wasDismissedAndMobile = dismissedState.value && isMobile;
    const shouldShow =
        !profilePaymentStatus.isLoading &&
        !profilePaymentStatus.isPaid &&
        !wasDismissedAndMobile &&
        !dismissedState.isLoading &&
        appType === 'consumer' &&
        isProfileOwner;
    const canDismiss = isMobile; // only dismissible on mobile
    const onDismiss = () => {
        if (!canDismiss) return;

        dismissedState.setValue(true);
    };

    return {
        canDismiss,
        onDismiss,
        shouldShow,
        messaging: {
            message: intl('message'),
            buttonText: intl('buttonText'),
        },
    };
};
